import { useEffect, useState } from "react";
import {
  App,
  Button,
  Card,
  Form,
  Input,
  Modal,
  Select,
  Space,
  Table,
  Tag,
  Typography,
} from "antd";
import {
  DownloadOutlined,
  EyeOutlined,
  PlusOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { crmApi } from "@/core/api/crmApi";
import { exportApi } from "@/core/api/exportApi";
import { tenantFeaturesApi } from "@/core/api/tenantFeaturesApi";
import { useAuthStore } from "@/core/auth/authStore";
import { hasPermission } from "@/core/auth/permissions";
import type { Contact, CreateContactRequest } from "@/core/types/crm";
import { tenantFeatureCodes } from "@/core/types/features";
import { PageHeader } from "@/shared/components/PageHeader";
import { formatDateTime } from "@/core/utils/date";
import { CustomerProfileDrawer } from "../components/CustomerProfileDrawer";
import { useSearchParams } from "react-router";

interface ContactForm {
  firstName?: string;
  lastName?: string;
  mobile?: string;
  email?: string;
  status: "active" | "inactive";
}
export const ContactsPage = (): React.ReactElement => {
  const { message } = App.useApp();
  const client = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const permissions = useAuthStore((state) => state.permissions);
  const [exporting, setExporting] = useState(false);
  const search = searchParams.get("search")?.trim() || "";
  const [searchInput, setSearchInput] = useState(search);
  const [open, setOpen] = useState(false);
  const [selectedContact, setSelectedContact] = useState<Contact | null>(null);
  const [form] = Form.useForm<ContactForm>();
  const query = useQuery({
    queryKey: ["contacts", search],
    queryFn: () => crmApi.contacts({ search, pageSize: 50 }),
  });
  useEffect(() => setSearchInput(search), [search]);
  useEffect(() => {
    const selectedId = searchParams.get("contact");
    const contact = query.data?.items.find((item) => item.id === selectedId);
    if (contact) setSelectedContact(contact);
  }, [query.data?.items, searchParams]);
  const applySearch = (value: string): void => {
    const next = new URLSearchParams(searchParams);
    const term = value.trim();
    if (term) next.set("search", term);
    else next.delete("search");
    next.delete("contact");
    setSearchParams(next, { replace: true });
  };
  const featuresQuery = useQuery({
    queryKey: ["tenant-features"],
    queryFn: tenantFeaturesApi.current,
  });
  const enabledFeatures = new Set(
    featuresQuery.data
      ?.filter((feature) => feature.isEnabled)
      .map((feature) => feature.code) ?? [],
  );
  const canViewRetailHistory =
    enabledFeatures.has(tenantFeatureCodes.products) &&
    enabledFeatures.has(tenantFeatureCodes.customerInterest);
  const canRevokeLanding =
    enabledFeatures.has(tenantFeatureCodes.personalLanding) &&
    hasPermission(permissions, "contacts.update");
  const create = useMutation({
    mutationFn: (r: CreateContactRequest) => crmApi.createContact(r),
    onSuccess: () => {
      message.success("مخاطب ثبت شد.");
      setOpen(false);
      form.resetFields();
      void client.invalidateQueries({ queryKey: ["contacts"] });
    },
    onError: () => message.error("ثبت مخاطب ناموفق بود."),
  });
  const submit = (v: ContactForm): void => {
    const points = [
      { type: "mobile" as const, value: v.mobile },
      { type: "email" as const, value: v.email },
    ]
      .filter((x): x is { type: "mobile" | "email"; value: string } =>
        Boolean(x.value?.trim()),
      )
      .map((x) => ({
        ...x,
        value: x.value.trim(),
        isPrimary: true,
        isVerified: false,
      }));
    create.mutate({
      firstName: v.firstName,
      lastName: v.lastName,
      status: v.status,
      contactPoints: points,
    });
  };
  return (
    <div className="page-stack">
      <PageHeader
        title="مخاطبان"
        description="اطلاعات اشخاص و راه‌های ارتباطی تأییدشده"
        extra={
          <Space>
            <>
              {hasPermission(permissions, "tenancy.manage") ? (
                <Button
                  icon={<DownloadOutlined />}
                  loading={exporting}
                  onClick={async () => {
                    setExporting(true);
                    try {
                      await exportApi.contacts();
                      message.success("فایل Excel مخاطبان آماده شد.");
                    } catch {
                      message.error("دریافت فایل Excel مخاطبان ناموفق بود.");
                    } finally {
                      setExporting(false);
                    }
                  }}
                >
                  خروجی Excel مخاطبان
                </Button>
              ) : null}
            </>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => setOpen(true)}
            >
              مخاطب جدید
            </Button>
          </Space>
        }
      />
      <Card variant="borderless">
        <Input
          allowClear
          prefix={<SearchOutlined />}
          placeholder="جست‌وجوی نام، موبایل یا ایمیل"
          onChange={(event) => {
            setSearchInput(event.target.value);
            if (!event.target.value) applySearch("");
          }}
          onPressEnter={(event) => applySearch(event.currentTarget.value)}
          style={{ maxWidth: 420 }}
          value={searchInput}
        />
      </Card>
      <div className="grid gap-3 md:hidden">
        {query.isLoading ? (
          [1, 2, 3].map((item) => (
            <div
              className="h-28 animate-pulse rounded-2xl bg-slate-200"
              key={item}
            />
          ))
        ) : query.data?.items.length ? (
          query.data.items.map((contact) => (
            <button
              className="rounded-2xl border border-slate-200 bg-white p-4 text-right shadow-sm"
              key={contact.id}
              onClick={() => setSelectedContact(contact)}
              type="button"
            >
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="font-black text-slate-800">
                    {`${contact.firstName || ""} ${contact.lastName || ""}`.trim() ||
                      "بدون نام"}
                  </p>
                  <div
                    className="mt-2 space-y-1 font-sans text-xs text-slate-500"
                    dir="ltr"
                  >
                    {contact.contactPoints.map((point) => (
                      <p key={point.id}>{point.value}</p>
                    ))}
                  </div>
                </div>
                <Tag color={contact.status === "active" ? "green" : "default"}>
                  {contact.status === "active" ? "فعال" : "غیرفعال"}
                </Tag>
              </div>
              <p className="mt-3 border-t border-slate-100 pt-3 text-xs font-bold text-brand-600">
                مشاهده پرونده و تعاملات
              </p>
            </button>
          ))
        ) : (
          <div className="rounded-2xl border border-dashed border-slate-200 bg-white p-8 text-center text-sm text-slate-400">
            مخاطبی ثبت نشده است.
          </div>
        )}
      </div>
      <Card className="hidden md:block" variant="borderless">
        <Table<Contact>
          rowKey="id"
          loading={query.isLoading}
          dataSource={query.data?.items}
          pagination={false}
          scroll={{ x: "max-content" }}
          columns={[
            {
              title: "نام",
              render: (_, x) => (
                <Typography.Text strong>
                  {`${x.firstName || ""} ${x.lastName || ""}`.trim() ||
                    "بدون نام"}
                </Typography.Text>
              ),
            },
            {
              title: "راه‌های ارتباطی",
              render: (_, x) => (
                <Space orientation="vertical" size={0}>
                  {x.contactPoints.map((p) => (
                    <Typography.Text key={p.id} dir="ltr">
                      {p.value}{" "}
                      {p.isVerified ? <Tag color="green">تأییدشده</Tag> : null}
                    </Typography.Text>
                  ))}
                </Space>
              ),
            },
            {
              title: "وضعیت",
              dataIndex: "status",
              render: (v) => (
                <Tag color={v === "active" ? "green" : "default"}>
                  {v === "active" ? "فعال" : "غیرفعال"}
                </Tag>
              ),
            },
            {
              title: "تاریخ ثبت",
              dataIndex: "createdAtUtc",
              render: formatDateTime,
            },
            {
              title: "پرونده",
              fixed: "left",
              render: (_, contact) => (
                <Button
                  icon={<EyeOutlined />}
                  onClick={() => setSelectedContact(contact)}
                >
                  مشاهده
                </Button>
              ),
            },
          ]}
          locale={{ emptyText: "مخاطبی ثبت نشده است." }}
        />
      </Card>
      <Modal
        open={open}
        title="ثبت مخاطب"
        onCancel={() => setOpen(false)}
        onOk={() => form.submit()}
        confirmLoading={create.isPending}
        destroyOnHidden
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={{ status: "active" }}
          onFinish={submit}
        >
          <Space.Compact block>
            <Form.Item name="firstName" label="نام" style={{ width: "50%" }}>
              <Input />
            </Form.Item>
            <Form.Item
              name="lastName"
              label="نام خانوادگی"
              style={{ width: "50%" }}
            >
              <Input />
            </Form.Item>
          </Space.Compact>
          <Form.Item name="mobile" label="موبایل">
            <Input dir="ltr" />
          </Form.Item>
          <Form.Item name="email" label="ایمیل">
            <Input dir="ltr" />
          </Form.Item>
          <Form.Item name="status" label="وضعیت">
            <Select
              options={[
                { value: "active", label: "فعال" },
                { value: "inactive", label: "غیرفعال" },
              ]}
            />
          </Form.Item>
        </Form>
      </Modal>
      {selectedContact ? (
        <CustomerProfileDrawer
          canManageProfile={hasPermission(permissions, "contacts.update")}
          canRevokeLanding={canRevokeLanding}
          canViewRetailHistory={canViewRetailHistory}
          contact={selectedContact}
          onClose={() => setSelectedContact(null)}
        />
      ) : null}
    </div>
  );
};
